import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal hook for mobile devices.
 * Toggles `data-scroll-active="true"` when the element is in the center 60% of the viewport.
 * Only activates on screens < 768px. Uses IntersectionObserver (no scroll listeners).
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Only on mobile
    if (window.innerWidth >= 768) return;

    // Respect reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.setAttribute('data-scroll-active', 'true');
        } else {
          el.removeAttribute('data-scroll-active');
        }
      },
      {
        rootMargin: '-35% 0px -35% 0px', // center 30% of viewport
        threshold: 0,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}
